/**
 * Store pour la gestion des groupes de configuration de tâche
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useTaskConfigurationGroupStore = defineStore('taskConfigurationGroup', (() => {

    // State de la liste des groupes (total et items)
    const groupList = ref({});

    // State des groupes chargés
    const groups = ref([]);

    /**
     * Retourne la liste complète des groupes chargés
     * @return array
     */
    const getGroups = computed(() => groups.value);

    /**
     * Retourne les données de la liste des groupes (total, groups)
     * @return object
     */
    const getGroupList = computed(() => groupList.value);

    /**
     * Retourne le groupe dont l'identifiant est en paramètre
     * @param int groupId
     * @return object|null
     */
    const getTaskConfigurationGroupById = computed(() => {
        return ((groupId) => {
            const group = groups.value.filter((groupStore) => groupStore.id === groupId).at(0);
            return (group ?? null);
        });
    });

    /**
     * Retourne les configurations de tâche du groupe en paramètre
     * @param object group
     * @return array|null
     */
    const getTaskConfigurationOfGroup = computed(() => {
        return ((group) => {
            const groupFromStore = groups.value.find((groupStore) => groupStore.id === group.id);
            if(! groupFromStore) {
                return null;
            }
            return (groupFromStore.taskConfigurations ?? null);
        });
    })

    /**
     * Chargement de la liste des groupes
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadGroupList = ((params = {}) => {

        const path = '/api/task-configurations/groups';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetGroupList();
                }

                updateGroupList(response.data.data);

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement de la liste des configurations de tâches du groupe en paramètre
     * @param object group
     * @return Promise
     */
    const loadTaskConfigurationOfGroup = ((group) => {

        const groupData = getTaskConfigurationOfGroup.value(group);
        if(groupData !== null) {
            return Promise.resolve();
        }

        const path = '/api/admin/task-configurations/groups/:group'.replace(':group', group.id);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.group) {
                updateGroup(response.data.group);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour de la liste des groupes avec les données en paramètre
     * @return void
     */
    const updateGroupList = ((data) => {

        if(groupList.value.total === undefined) {
            groupList.value = {
                total: data.total,
                groups: data.items
            };

            data.items.forEach((group) => {
                updateGroup(group);
            });

        } else {
            groupList.value.total = data.total;

            const currentGroups = groupList.value.groups;
            const newGroups = data.items.filter((item) => ! currentGroups.find((current) => current.id === item.id));
            groupList.value.groups = [ ...currentGroups, ...newGroups ];

            newGroups.forEach((group) => {
                updateGroup(group);
            });
        }

    });

    /**
     * Mise à jour des données dans le store du groupe en paramètre
     * @param object group
     * @return void
     */
    const updateGroup = ((group) => {

        const groupId = group.id;

        const index = groups.value.findIndex((groupFromState) => groupFromState.id === groupId);
        if(index !== -1) {
            groups.value[index] = { ...groups.value[index], ...group };
        } else {
            groups.value.push(group);
        }

        // Synchronisation avec les éléments de la liste
        const listData = groupList.value;
        if(! group.isChild && listData.total !== undefined) {
            const listGroups = listData.groups || [];
            const indexInList = listGroups.findIndex((groupFromState) => groupFromState.id === groupId);
            if(indexInList !== -1) {
                listGroups[indexInList] = { ...listGroups[indexInList], ...group };
            } else {
                groupList.value.total += 1;
                listGroups.push(group);
            }

            groupList.value.groups = listGroups;
        }


        if(group.children && group.children.length > 0) {
            group.children.forEach((child) => {
                updateGroup(child);
            });
        }

    });

    /**
     * Réinitialisation de la liste des groupes
     * @return void
     */
    const resetGroupList = (() => {
        groupList.value = {};
    });

    /**
     * Retire la configuration de tâche du groupe en paramètre
     * @param object group
     * @param object taskConfiguration
     * @return Promise
     */
    const unlinkTaskConfiguration = ((group, taskConfiguration) => {

        const path = '/api/admin/task-configurations/groups/:groupId/task-configurations/:taskConfigurationId'
            .replace(':groupId', group.id)
            .replace(':taskConfigurationId', taskConfiguration.id)
        ;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success && response.data.group) {
                updateGroup(response.data.group);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    return {
        getGroups,
        getGroupList,
        getTaskConfigurationGroupById,
        getTaskConfigurationOfGroup,
        loadTaskConfigurationOfGroup,
        loadGroupList,
        updateGroup,
        unlinkTaskConfiguration
    };
}));
